import { Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { Confetti } from "src/components/Confetti";
import { Card } from "src/components/Layout/Card";
import { ParentRemoteDataLayout } from "src/components/Layout/Parent/ParentRemoteDataLayout";
import { ParentFormsLayout } from "src/components/Layout/ParentFormsLayout";
import { useGetOffersForMe } from "src/generated/hooks/form-service/queries";
import { useOrganization, useTimezoneName } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import AdmissionsSvg from "src/images/admissions.svg?react";
import { WEGLOT_SKIP_CLASS } from "src/plugins/weglot/constants";
import {
  formatOfferExpirationAt,
  hasSomeUnexpiredOffers,
  isOfferExpired,
} from "src/services/form/offer";
import { maybePluralize } from "src/services/format";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { AcceptOffer } from "./formTemplates/components/ActionItem/AcceptOffer";
import { DeclineOffer } from "./formTemplates/components/ActionItem/DeclineOffer";
import { GET_OFFERS_FORM_CONTEXT } from "./formTemplates/graphql/queries";

export const Offers: React.FC = () => {
  const { appId: formId } = useParams();
  const organization = useOrganization();
  const [isActionPending, setIsActionPending] = React.useState(false);
  const timezoneName = useTimezoneName();
  const backLink = {
    label: "Back",
    url: organization.map(Url.Parent.index).withDefault("#"),
  };

  const { remoteData: formContextData } = useRemoteDataQuery<
    GQL.GetOffersFormContext,
    GQL.GetOffersFormContextVariables
  >(GET_OFFERS_FORM_CONTEXT, {
    variables: {
      form_id: formId ?? "",
    },
    fetchPolicy: "cache-and-network",
    skip: !formId,
  });

  const offersQuery = useGetOffersForMe(
    {
      path: {
        formId: formId ?? "",
      },
      query: {
        status: ["Offered", "Accepted"],
      },
    },
    [formId],
    {
      enabled: !!formId,
      refetchOnMount: "always",
    }
  );

  const offersData = React.useMemo(
    () =>
      RD.toTuple(
        formContextData.mapError((error) => error as Error),
        RD.fromRestResult(offersQuery)
          .mapError(RD.errorResponseToError)
          .mapError((error) => error ?? new Error("Error fetching offers."))
      ),
    [formContextData, offersQuery]
  );

  const OffersError = (
    <Flex direction="column" alignItems="center" gap={4} mt="30%">
      <AdmissionsSvg aria-hidden />

      <Text color="gray.600" fontSize="lg">
        We couldn't find this form.
      </Text>
      <Button
        as={Link}
        to={backLink.url}
        variant="outline"
        colorScheme="gray"
        size="sm"
      >
        Back
      </Button>
    </Flex>
  );

  return (
    <ParentRemoteDataLayout remoteData={offersData}>
      {([context, offersResponse]) => {
        const form = context.form[0];
        if (!form) {
          return (
            <ParentFormsLayout heading="Offers" backLink={backLink}>
              {OffersError}
            </ParentFormsLayout>
          );
        }

        const rankingEnabled =
          form.form_template.sections[0]?.schools_ranking_section
            ?.ranking_enabled ?? false;

        const studentName = form.person.first_name ?? "Your student";
        const enrollmentPeriod = form.form_template.enrollment_period.name;
        const offersBySchoolId = new Map(
          offersResponse.data.offers
            .filter((offer) => offer.status === "Offered")
            .map((offer) => [offer.schoolId, offer])
        );

        const offeredSchoolRanks = form.form_school_ranks.flatMap(
          (schoolRank) => {
            const offer = offersBySchoolId.get(schoolRank.school.id);
            return offer ? [{ schoolRank, offer }] : [];
          }
        );

        const offerMessage = `${studentName} has received ${maybePluralize(
          offeredSchoolRanks.length,
          "an offer",
          `${offeredSchoolRanks.length} offers`
        )} for ${enrollmentPeriod}!`;

        const hasUnexpiredOffers = hasSomeUnexpiredOffers(
          offeredSchoolRanks.map(({ offer }) => ({
            expires_at: offer.expiresAt,
            timezone_name: timezoneName,
          }))
        );

        const accepted = offersResponse.data.offers.find(
          (offer) => offer.status === "Accepted"
        );
        const acceptedOfferSchoolName = accepted
          ? form.form_school_ranks.find(
              (rank) => rank.school.id === accepted.schoolId
            )?.school.name
          : undefined;

        return (
          <ParentFormsLayout
            heading={`Offers for ${studentName}`}
            backLink={backLink}
          >
            {offeredSchoolRanks.length > 0 ? (
              <Flex direction="column" gap={5}>
                <Card
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  bg="green.500"
                >
                  {hasUnexpiredOffers && <Confetti />}
                  <AdmissionsSvg />
                  <Text textAlign="center" color="white">
                    {offerMessage}
                  </Text>
                </Card>
                {offeredSchoolRanks.map(({ schoolRank, offer }) => {
                  const offerHasExpired = isOfferExpired({
                    expires_at: offer.expiresAt,
                    timezone_name: timezoneName,
                  });

                  return (
                    <Card key={schoolRank.school.id} boxShadow="md">
                      <Flex direction="column" alignItems="flex-start" pb={4}>
                        {rankingEnabled && (
                          <Text color="blackAlpha.700">
                            {convertToOrdinal(schoolRank.rank + 1)} choice{" "}
                            {schoolRank.rank === 0 ? <>&#127881;</> : ""}
                          </Text>
                        )}
                        <Text
                          className={WEGLOT_SKIP_CLASS}
                          color="gray.700"
                          fontWeight="bold"
                        >
                          {schoolRank.school.name}
                        </Text>
                        {offer.expiresAt && (
                          <Text fontSize="sm">
                            {formatOfferExpirationAt({
                              expires_at: offer.expiresAt,
                              timezone_name: timezoneName,
                            })}
                          </Text>
                        )}
                      </Flex>
                      <Flex
                        direction="row"
                        gap={4}
                        justifyContent="space-between"
                      >
                        <DeclineOffer
                          formId={form.id}
                          offerId={offer.id}
                          school={schoolRank.school.name}
                          isButton
                          isDisabled={offerHasExpired || isActionPending}
                          onActionPendingChange={setIsActionPending}
                        />
                        <AcceptOffer
                          formId={form.id}
                          formKey={form.form_template.key}
                          formTemplateId={form.form_template_id}
                          acceptedOfferSchoolName={acceptedOfferSchoolName}
                          offerId={offer.id}
                          school={schoolRank.school.name}
                          schoolReferenceId={schoolRank.school.reference_id}
                          studentName={studentName}
                          enrollmentPeriodKey={
                            form.form_template.enrollment_period.key
                          }
                          isButton
                          isDisabled={offerHasExpired || isActionPending}
                          onActionPendingChange={setIsActionPending}
                        />
                      </Flex>
                    </Card>
                  );
                })}
              </Flex>
            ) : (
              <Flex direction="column" alignItems="center" gap={4} mt="30%">
                <AdmissionsSvg />
                <Text color="gray.600" fontSize="lg">
                  {studentName} has no more offers
                </Text>
                <Button
                  as={Link}
                  to={backLink.url}
                  variant="outline"
                  colorScheme="gray"
                  size="sm"
                >
                  Back
                </Button>
              </Flex>
            )}
          </ParentFormsLayout>
        );
      }}
    </ParentRemoteDataLayout>
  );
};

const convertToOrdinal = (n: number) => {
  const suffix: string[] = ["th", "st", "nd", "rd"];
  const value = n % 100;
  return (
    n.toString() + (suffix[(value - 20) % 10] || suffix[value] || suffix[0])
  );
};
